<template>
  <div class="art-navigation light-navigation">
    <a href :class="{ disabled: isFirst }" @click.prevent.stop="previous()" v-show="!hideLinks">
      <i class="icon-PREVIOUS"></i>
    </a>
    <a href :class="{ disabled: isLast }" @click.prevent.stop="next()" v-show="!hideLinks">
      <i class="icon-Next"></i>
    </a>
    <router-link :to="{ name: 'Artworks', query: queryParams(), params: filterParams }" class="art-close">
      <i class="icon-CLOSE"></i>
    </router-link>
  </div>
</template>
<script>
import { mapGetters, mapActions, mapState } from 'vuex';
import { pageSize } from 'config';
import pick from 'lodash.pick';
import FilterBuilder from 'lib/filterBuilder';

const CLOSE_QUERY = ['layout', 'page', 'ids', 'movement_id', 'favorites', 'sort'];
const SEARCH_QUERY = CLOSE_QUERY.concat(['filters']);

export default {
  name: 'ArtworkNavigation',
  mounted() {
    window.addEventListener('keydown', this.hotkeyEvent);
  },
  destroyed() {
    window.removeEventListener('keydown', this.hotkeyEvent);
  },
  data() {
    return {
      parentIndex: null,
    };
  },
  computed: {
    ...mapGetters('artworks', ['artworks', 'artworksArray', 'filteredCount']),
    ...mapGetters('artworks', ['artwork', 'currentImage', 'images', 'elements', 'getElementById']),
    ...mapGetters('params', ['page', 'layout', 'filters']),
    ...mapGetters('textNotes', ['form']),
    ...mapState('route', ['query']),
    filterParams() {
      return {
        filters: FilterBuilder.toURLParam(FilterBuilder.fromParam(this.$route.query.filters)),
      };
    },
    loadedSize() {
      return Object.keys(this.artworks).length;
    },
    pageSize() {
      return pageSize[this.layout] || pageSize.grid;
    },
    totalPages() {
      return Math.ceil(this.filteredCount / this.pageSize);
    },
    isLastPage() {
      return this.page === this.totalPages;
    },
    isFirstPage() {
      return this.page === 1;
    },
    isLastIndex() {
      const length = this.artworksArray.length;
      return this.artworksArray[this.currentIndex] === this.artworksArray[length - 1];
    },
    currentIndex() {
      return this.artworksArray.map((a) => a._id).indexOf(this.artwork._id);
    },
    isLast() {
      return this.isLastIndex && this.isLastPage;
    },
    isFirst() {
      return this.currentIndex <= 0 && this.isFirstPage;
    },
    hideLinks() {
      const { nonav } = this.$route.query;
      return nonav || this.artworks.length < 2;
    },
    elementIndex() {
      const element = this.getElementById(this.artwork._id);
      const elementIndex = this.elements.indexOf(element);
      return elementIndex;
    },
    routeIndex() {
      const { index } = this.query;
      const parsedIndex = parseInt(index);
      if (!isNaN(parsedIndex) && typeof parsedIndex === 'number') return parsedIndex;
      else {
        return -1;
      }
    },
    isSeparable() {
      return this.artwork.type === 'separable';
    },
    isNonseparable() {
      return this.artwork.type === 'nonseparable';
    },
  },
  methods: {
    ...mapActions('artworks', ['assign', 'fetchArtworks']),
    hotkeyEvent(e) {
      if (this.form.hasOwnProperty('content')) {
        return;
      }
      if (e.keyCode === 37) {
        if (this.isPreviosElementExists(this.images, this.currentImage)) {
          this.$store.commit('artworks/assign', { name: 'currentImage', data: this.currentImage - 1 });
        } else if (this.isPreviosElementExists(this.elements, this.elementIndex) && this.isSeparable) {
          if (!this.parentIndex) {
            this.parentIndex = this.currentIndex;
          }
          const artwork = this.elements[this.elementIndex - 1];
          this.assignArtwork(artwork);
        } else if (this.isPreviosElementExists(this.elements, this.routeIndex) && this.isNonseparable) {
          const query = Object.assign({}, this.$route.query, { index: this.routeIndex - 1 });
          this.$router.push({ query });
        } else if ((this.routeIndex === 0 && this.isNonseparable) || (this.elementIndex == 0 && this.isSeparable)) {
          const query = pick(Object.assign({}, this.$route.query, {}), SEARCH_QUERY);
          this.$router.push({ query: Object.assign({}, query, { isHeader: true }) });
          window.scrollTo(0, 0);
        } else {
          this.previous();
        }
      } else if (e.keyCode === 39) {
        if (this.isNextElementExists(this.images, this.currentImage)) {
          this.$store.commit('artworks/assign', { name: 'currentImage', data: this.currentImage + 1 });
        } else if (this.isNextElementExists(this.elements, this.elementIndex) && this.isSeparable) {
          if (!this.parentIndex) {
            this.parentIndex = this.currentIndex;
          }
          const artwork = this.elements[this.elementIndex + 1];
          this.assignArtwork(artwork);
        } else if (this.isNextElementExists(this.elements, this.routeIndex) && this.isNonseparable) {
          const query = Object.assign({}, this.$route.query, { index: this.routeIndex + 1 });
          delete query.isHeader;
          this.$store.commit('artworks/assign', { name: 'currentImage', data: 0 });
          this.$router.push({ query });
        } else {
          this.next();
        }
      } else if (e.keyCode === 27) {
        this.close();
      }
    },
    async previous() {
      if (this.isFirst) return;

      const currentIndex = this.parentIndex || this.currentIndex;
      let page = this.page;
      let index = currentIndex - 1;

      if (currentIndex === 0) {
        page = this.page - 1;
        index = this.pageSize - 1;
      }

      this.parentIndex = null;
      this.findArtwork(index, { page });
      scroll(0, 0);
    },
    async next() {
      if (this.isLast) return;

      const currentIndex = this.parentIndex || this.currentIndex;
      let index = currentIndex + 1;
      let page = this.page;

      if (currentIndex + 1 === this.loadedSize) {
        page = this.page + 1;
        //index = 0;
      }
      this.parentIndex = null;
      this.findArtwork(index, { page });
      scroll(0, 0);
    },
    async findArtwork(index, query = {}) {
      const { page } = query;
      if (page && this.page !== page) {
        const query = this.$route.query;

        this.$router.push({
          query: { ...query, page },
        });
        const direction = 1;
        await this.fetchArtworks(direction);
      }

      const artwork = this.artworksArray[index];
      this.assignArtwork(artwork, query);
    },

    assignArtwork(artwork, query) {
      const slug = artwork.slug ?? artwork._id;
      //this.$store.commit('artworks/assign', { name: 'artwork', data: artwork });
      const searchContext = pick(Object.assign({}, this.$route.query, query), SEARCH_QUERY);
      this.$store.commit('artworks/assign', { name: 'artworkId', data: artwork._id });
      this.$store.commit('artworks/assign', { name: 'currentImage', data: 0 });
      this.$router.push({
        name: 'artwork',
        params: { id: slug },
        query: searchContext,
      });
    },
    isPreviosElementExists(elements, index) {
      return elements.length > 1 && index > 0;
    },
    isNextElementExists(elements, index) {
      return elements.length > 1 && index < elements.length - 1;
    },
    queryParams() {
      return pick(Object.assign({}, this.$route.query, { silent: true }), CLOSE_QUERY);
    },
    close() {
      this.$router.push({
        name: 'Artworks',
        query: this.queryParams(),
        params: this.filterParams,
      });
    },
  },
};
</script>
